import { Area, MenuTypes, PromotionTypes } from '@/constants'
import type {
  BannerList,
  Category,
  CategoryFeature,
  CategoryListResponse,
  CouponCustomerItem,
  CouponCustomerListRequest,
  CouponCustomerListResponse,
  CustomerCreateRequest,
  CustomerCreateRespone,
  CustomerListRequest,
  CustomerListResponse,
  FlashSale,
  KeywordsResponse,
  Menu,
  Order,
  Path,
  Post,
  Product,
  ProductDetailResponse,
  ProductListResponse,
  ProductReviewResponse,
  Promotion,
  PromotionDetailResponse,
  PromotionProductCarousel,
  Province,
  QueryParam,
  ReasonCancel,
  Region,
  ResponseApi,
  Store,
  SystemConfig,
  TransactionListRequest,
  TransactionListResponse,
  UserAddress,
  Wallet,
  WithdrawRequest,
} from '@/types'
import sortBy from 'lodash/sortBy'
import { api } from './api'
import { convertQueryParam } from './next-util'
import {
  toBanner,
  toBestSeller,
  toCategoryFeatures,
  toFlashSale,
  toFlashSales,
  toHomepageProductCarousel,
  toProductForYou,
  toProductTrending,
  toPromotions,
  toYourPromotions,
} from './transformer'

// type RequestParams = {
//   [key: string]: string | undefined
// }

export const getPromotions = async (): Promise<Promotion | null> => {
  const res = await api(`api/promotions?sortType=asc&page=1&limit=20`, {
    baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return toPromotions(res.data)
  }
  return null
}

export const getProductTrending = async (): Promise<Promotion | null> => {
  const res = await api(`api/promotions?&page=1&limit=20&type=trending`, {
    baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return toProductTrending(res.data)
  }
  return null
}

export const getBestSeller = async (): Promise<Promotion | null> => {
  const res = await api(
    `api/promotions?&page=1&limit=20&type=${PromotionTypes.BEST_SELLER}`,
    {
      baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return toBestSeller(res.data)
  }
  return null
}

export const getBestSellerSSR = async (): Promise<Promotion | null> => {
  const res = await api(
    `api/promotions?&page=1&limit=20&type=${PromotionTypes.BEST_SELLER}`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return toBestSeller(res.data)
  }
  return null
}
export const getHomepageProductCarousel = async (): Promise<
  PromotionProductCarousel[] | null
> => {
  const res = await api(
    `api/promotions?&page=1&productLimit=54&type=${PromotionTypes.LIST_HOT}`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )

  if (res && !(res instanceof Error)) {
    return toHomepageProductCarousel(res.data)
  }
  return null
}

export const getFlashSale = async (): Promise<FlashSale | null> => {
  const res = await api(`api/flash-sales?page=1&limit=20`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return toFlashSales(res.data)
  }
  return null
}

export const getFlashSaleSSR = async (
  viewAt: string
): Promise<FlashSale | null> => {
  const res = await api(
    `api/flash-sales?page=1&limit=20${viewAt ? '&viewAt=' + viewAt : ''}`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return toFlashSales(res.data)
  }
  return null
}

export const getPosts = async (id: string): Promise<Post[] | null> => {
  const res = await api(`api/posts?page=1&limit=9&catSlugs=${id}`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return []
}

export const getPostsByType = async (type: string): Promise<Post[] | null> => {
  const res = await api(`api/posts?page=1&limit=9&type=${type}`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data?.data || []
  }
  return []
}

export const getPostsByCategory = async (
  id: string
): Promise<Post[] | null> => {
  const res = await api(
    `api/posts?page=1&limit=9&display=true&catSlugs=${id}`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return []
}
export const getFeaturePost = async (): Promise<Post[] | null> => {
  const res = await api(
    `api/posts?page=1&limit=9&display=true&highlight=true`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return res?.data?.data
  }
  return []
}
export const getPostCategory = async (): Promise<Post[] | null> => {
  const res = await api(`api/post-cat?display=true&sort=order`, {
    baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return []
}
export const getPostFilterCategory = async (): Promise<Array<{
  item: Post
  data: Post[]
}> | null> => {
  try {
    const res = await api(`api/post-cat?display=true&sort=order`, {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    })
    if (res && !(res instanceof Error)) {
      const responses = await Promise.all(
        res.data.map(async (item: Post) => {
          const resCate = await api(
            `api/posts?page=1&limit=9&catSlugs=${item.slugs.value.vi}`,
            {
              baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
              method: 'get',
            }
          )
          return {
            item,
            data: resCate && !(resCate instanceof Error) ? resCate.data : [],
          }
        })
      )
      return responses
    }
  } catch (error) {
    console.error(error)
    return null
  }
  return []
}

export const getAllPosts = async (): Promise<Post[] | null> => {
  const res = await api(`api/posts`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data.data
  }
  return []
}

export const getAllPostsSSR = async (): Promise<Post[] | null> => {
  const res = await api(`api/posts`, {
    baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data.data
  }
  return []
}
export const getStores = async (): Promise<Store[] | null> => {
  const res = await api(`api/store`, {
    baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return []
}
export const getStoresSSR = async (): Promise<Store[] | null> => {
  const res = await api(`api/store`, {
    baseURL: process.env.NEXT_PRIVATE_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return []
}

export const getProvinces = async (): Promise<Province[] | null> => {
  const res = await api(`api/area?level=${Area.PROVINCE}`, {
    baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data.map((e: Province) => ({
      ...e,
      ...{ name: e.name.replace('Tỉnh ', '').replace('Thành phố ', '') },
    }))
  }
  return []
}
export const getProvincesSSR = async (): Promise<Province[] | null> => {
  const res = await api(`api/area?level=${Area.PROVINCE}`, {
    baseURL: process.env.NEXT_PRIVATE_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data.map((e: Province) => ({
      ...e,
      ...{ name: e.name.replace('Tỉnh ', '').replace('Thành phố ', '') },
    }))
  }
  return []
}
export const getSaleRegion = async (): Promise<Region[] | null> => {
  const res = await api(`api/sale-region`, {
    baseURL: process.env.NEXT_PRIVATE_ECOMMERCE_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return []
}

export const getMenus = async (): Promise<Menu[] | null> => {
  const res = await api(
    `api/menu-item?menuTypes=${MenuTypes.GLOBAL_TOP}&sortBy=order&sortType=asc`,
    {
      baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return sortBy(res.data, 'order').map((e: Menu) => {
      e.children = sortBy(e.children, 'order')
      return e
    })
  }
  return []
}

export const getQuickMenu = async (): Promise<Menu[][] | null> => {
  const res = await api(`api/menu-item?menuTypes=${MenuTypes.GLOBAL_QUICK}`, {
    baseURL: process.env.NEXT_PRIVATE_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    const sortedData = sortBy(res.data, 'order')

    const map = new Map<string, Menu[]>()
    sortedData?.forEach((item: Menu) => {
      const menuId = item.menuId || ''
      if (map.has(menuId)) {
        map.get(menuId)?.push?.(item)
      } else {
        map.set(menuId, [item])
      }
    })

    return Array.from(map.values())
  }
  return []
}

export const getHeaderMenu = async (): Promise<Menu[] | null> => {
  const res = await api(`api/menu-item?menuTypes=${MenuTypes.GLOBAL_HEADER}`, {
    baseURL: process.env.NEXT_PRIVATE_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return sortBy(res.data, 'order')
  }
  return []
}

export const getBottomMenu = async (): Promise<Menu[] | null> => {
  const res = await api(`api/menu-item?menuTypes=${MenuTypes.GLOBAL_BOTTOM}`, {
    baseURL: process.env.NEXT_PRIVATE_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return sortBy(res.data, 'order')
  }
  return []
}

export const getCategoriesFeature = async (): Promise<
  CategoryFeature[] | null
> => {
  const res = await api(`api/product-cats?page=1&limit=20featured=true`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return toCategoryFeatures(res.data)
  }
  return []
}
export const getCategoriesFeatureSSR = async (): Promise<
  CategoryFeature[] | null
> => {
  const res = await api(`api/product-cats?page=1&limit=20featured=true`, {
    baseURL: process.env.NEXT_PRIVATE_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return toCategoryFeatures(res.data)
  }
  return []
}

export const getBanner = async (): Promise<BannerList[] | null> => {
  const res = await api(`api/banners`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return toBanner(res.data)
  }
  return []
}

export const getBannerSSR = async (): Promise<BannerList[] | null> => {
  const res = await api(`api/banners`, {
    baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return toBanner(res.data)
  }
  return []
}

export const getCategory = async (
  id: string | string[],
  queryParam?: QueryParam
): Promise<Category | null> => {
  const params = convertQueryParam(queryParam)
  const res = await api(`api/product-cat/${id}/detail?${params}&limit=40`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    if (res.data?.properties) {
      res.data.properties = sortBy(res.data.properties, 'order')
    }
    return res.data
  }
  return null
}

export const getCategorySSR = async (
  id: string | string[],
  queryParam?: QueryParam
): Promise<Category | null> => {
  const params = convertQueryParam(queryParam)
  const res = await api(`api/product-cat/${id}/detail?${params}&limit=40`, {
    baseURL: process.env.NEXT_PRIVATE_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    if (res.data?.properties) {
      res.data.properties = sortBy(res.data.properties, 'order')
    }
    return res.data
  }
  return null
}
export const getAllCategory = async (): Promise<Category[] | null> => {
  const res = await api(`api/product-cat`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    if (res.data?.order) {
      res.data.order = sortBy(res.data.order, 'order')
    }
    return res.data
  }
  return null
}
export const getAllCategorySSR = async (): Promise<Category[] | null> => {
  const res = await api(`api/product-cat`, {
    baseURL: process.env.NEXT_PRIVATE_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    if (res.data?.order) {
      res.data.order = sortBy(res.data.order, 'order')
    }
    return res.data
  }
  return null
}
export const getProductWithPaginate = async (
  queryParams: QueryParam
): Promise<ProductListResponse | null> => {
  const params = convertQueryParam(queryParams)
  const res = await api(`api/products?${params}`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return null
}
export const getProductWithPaginateSSR = async (
  queryParams: QueryParam
): Promise<ProductListResponse | null> => {
  const params = convertQueryParam(queryParams)
  const res = await api(`api/products?${params}`, {
    baseURL: process.env.NEXT_PRIVATE_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return null
}

export const getYourPromotions = async (
  queryParams?: QueryParam
): Promise<Promotion[] | null> => {
  const params = convertQueryParam(queryParams)
  const res = await api(
    `api/promotions?sortBy=order&sortType=asc&page=1&limit=20&type=${PromotionTypes.YOUR_PROMOTION}&${params}`,
    {
      baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return toYourPromotions(res.data)
  }
  return null
}
export const getYourPromotionsSRR = async (
  queryParams?: QueryParam
): Promise<Promotion[] | null> => {
  const params = convertQueryParam(queryParams)
  const res = await api(
    `api/promotions?sortBy=order&sortType=asc&page=1&limit=20&type=${PromotionTypes.YOUR_PROMOTION}&${params}`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return toYourPromotions(res.data)
  }
  return null
}

export const getProductForYou = async (): Promise<Promotion | null> => {
  const res = await api(
    `api/promotions?page=1&limit=20&productLimit=100&type=${PromotionTypes.PRODUCT_FOR_YOU}`,
    {
      baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return toProductForYou(res.data)
  }
  return null
}

export const getProductDetail = async (
  id: string,
  queryParams?: QueryParam
): Promise<ProductDetailResponse | null> => {
  const params = convertQueryParam(queryParams)
  const res = await api(`api/product/${id}/detail?${params}`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res
  }

  return null
}
export const getProductDetailSSR = async (
  id: string,
  queryParams?: QueryParam
): Promise<ProductDetailResponse | null> => {
  const params = convertQueryParam(queryParams)
  const res = await api(`api/product/${id}/detail?${params}`, {
    baseURL: process.env.NEXT_PRIVATE_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res
  }

  return null
}

export const getPageDetail = async (
  path: string[],
  queryParams?: QueryParam
): Promise<ProductDetailResponse | ProductListResponse | null> => {
  const params = convertQueryParam(queryParams)
  if (path.length > 1) {
    if (path[1].indexOf('c') > -1) {
      const res = await api(`api/products?${params}`, {
        baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
        method: 'get',
      })
      if (res && !(res instanceof Error)) {
        return res.data
      }
    }
    if (path[1].indexOf('p') > -1) {
      const res = await api(`api/post/${path[0]}`, {
        baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
        method: 'get',
      })
      if (res && !(res instanceof Error)) {
        return res
      }
    }
    if (path[1].indexOf('b') > -1) {
      const res = await api(`api/post/${path[0]}`, {
        baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
        method: 'get',
      })
      if (res && !(res instanceof Error)) {
        return res
      }
    }
    return null
  }

  const res = await api(`api/product/${path[0]}/detail`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res
  }

  return null
}
export const getPageDetailSSR = async (
  path: string[],
  queryParams?: QueryParam
): Promise<ProductDetailResponse | ProductListResponse | null> => {
  const params = convertQueryParam(queryParams)
  if (path.length > 1) {
    if (path[1].indexOf('c') > -1) {
      const res = await api(`api/products?${params}`, {
        baseURL: process.env.NEXT_PRIVATE_CATALOG_API_URL,
        method: 'get',
      })
      if (res && !(res instanceof Error)) {
        return res.data
      }
    }
    if (path[1].indexOf('p') > -1) {
      const res = await api(`api/post/${path[0]}`, {
        baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
        method: 'get',
      })
      if (res && !(res instanceof Error)) {
        return res
      }
    }
    if (path[1].indexOf('b') > -1) {
      const res = await api(`api/post/${path[0]}`, {
        baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
        method: 'get',
      })
      if (res && !(res instanceof Error)) {
        return res
      }
    }
    return null
  }

  const res = await api(`api/product/${path[0]}/detail?${params}`, {
    baseURL: process.env.NEXT_PRIVATE_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res
  }

  return null
}

export const getPath = async (path: string): Promise<Path | null> => {
  const res = await api(`api/path-mapping/parsing?path=${path}`, {
    baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return null
}

export const getPathSSR = async (path: string): Promise<Path | null> => {
  const res = await api(`api/path-mapping/parsing?path=${path}`, {
    baseURL: process.env.NEXT_PRIVATE_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return null
}

export const getCategories = async (
  queryParams?: QueryParam
): Promise<CategoryListResponse | null> => {
  const params = convertQueryParam(queryParams)
  const res = await api(`api/product-cats?${params}`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return null
}
export const getCategoriesSSR = async (
  queryParams?: QueryParam
): Promise<CategoryListResponse | null> => {
  const params = convertQueryParam(queryParams)
  const res = await api(`api/product-cats?${params}`, {
    baseURL: process.env.NEXT_PRIVATE_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return null
}

export const getConfig = async (): Promise<SystemConfig[] | null> => {
  const res = await api(`api/configuration`, {
    baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return []
}
export const getConfigSSR = async (): Promise<SystemConfig[] | null> => {
  const res = await api(`api/configuration`, {
    baseURL: process.env.NEXT_PRIVATE_IAM_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }
  return []
}

export const getPromotionDetail = async (
  id?: string
): Promise<PromotionDetailResponse | null> => {
  const res = await api(`api/promotion/${id}`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
  })

  if (res && !(res instanceof Error)) {
    return res.data
  }

  return null
}
export const getPromotionDetailSSR = async (
  id: string
): Promise<PromotionDetailResponse | null> => {
  const res = await api(`api/promotion/${id}`, {
    baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }

  return null
}

export const getPostDetail = async (
  slug: string
): Promise<PromotionDetailResponse | null> => {
  const res = await api(`api/post/${slug}`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }

  return null
}
export const getPostDetailSSR = async (
  slug: string
): Promise<PromotionDetailResponse | null> => {
  const res = await api(`api/post/${slug}`, {
    baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }

  return null
}

export const getShippingAddress = async (): Promise<UserAddress[] | null> => {
  const res = await api(`api/user-contact?type=shipping`, {
    baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    method: 'get',
  })

  if (res && !(res instanceof Error)) {
    return res.data
  }
  return null
}
export const getShippingAddressDetail = async (
  params?: string
): Promise<UserAddress | null> => {
  const res = await api(`api/user-contact?ids=${params}&type=shipping`, {
    baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    method: 'get',
  })

  if (res && !(res instanceof Error)) {
    return res.data[0]
  }
  return null
}

export const getReasonCancel = async (): Promise<ReasonCancel[] | null> => {
  const res = await api(`api/order/cancelled-order-reason?display=true`, {
    method: 'get',
    baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
  })

  if (res && !(res instanceof Error)) {
    return res.data
  }
  return null
}

export const getProductReviews = async (
  params?: string
): Promise<ProductReviewResponse | null> => {
  const res = await api(`api/reviews?page=1&limit=5&${params}`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }

  return null
}

export const getAllProductReview = async (
  params?: string
): Promise<ProductReviewResponse | null> => {
  const res = await api(`api/reviews?page=1&limit=50&${params}`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (res && !(res instanceof Error)) {
    return res.data
  }

  return null
}

export const getFlashSaleDetail = async (
  id: string,
  viewAt: string
): Promise<FlashSale | null> => {
  const res = await api(
    `api/flash-sale/${id}${viewAt ? '?viewAt=' + viewAt : ''}`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return toFlashSale(res.data as FlashSale)
  }
  return null
}
export const getKeyword = async (): Promise<KeywordsResponse | null> => {
  const res = await api(
    `api/keywords?type=footerKeyword&sortBy=order&sortType=asc&page=1&limit=40`,
    {
      baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return res.data.data
  }
  return null
}

export const getExclusiveDeal = async (): Promise<Promotion | null> => {
  const res = await api(
    `api/promotions?&page=1&limit=40&type=${PromotionTypes.EXCLUSIVE_DEAL}`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return res?.data?.data[0] || null
  }
  return null
}

export const getExclusiveCatDeal = async (
  catId = ''
): Promise<Promotion | null> => {
  const res = await api(
    `api/promotions?&page=1&limit=40&type=${PromotionTypes.EXCLUSIVE_CAT_DEAL}&catIds=${catId}`,
    {
      baseURL: process.env.NEXT_PRIVATE_MARKETING_API_URL,
      method: 'get',
    }
  )
  if (res && !(res instanceof Error)) {
    return res?.data?.data[0] || null
  }
  return null
}

export const getViewedProducts = async (
  queryParams?: QueryParam
): Promise<Product[]> => {
  const params = convertQueryParam(queryParams)
  const res = await api(`api/viewed-products?${params}`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'get',
  })
  if (!(res instanceof Error) && res?.data) {
    return res.data
  }
  return []
}
export const deleteViewedProduct = async (id: string): Promise<boolean> => {
  const res = await api(`api/viewed-product/${id}`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'delete',
  })
  return res && !(res instanceof Error)
}
export const deleteViewedProducts = async (): Promise<boolean> => {
  const res = await api(`api/viewed-products`, {
    baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
    method: 'delete',
  })
  return res && !(res instanceof Error)
}

export const getAffiliateCustomers = async (
  request: CustomerListRequest
): Promise<CustomerListResponse> => {
  const res = await api(`api/user-customers`, {
    baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    method: 'get',
    params: request,
  })

  if (res?.data) {
    return res.data
  }

  throw new Error(res?.message || 'Fail to get customers')
}

export const addAffilicateCustomer = async (
  request: CustomerCreateRequest
): Promise<CustomerCreateRespone> => {
  const res = await api(`api/user-customer`, {
    baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    method: 'post',
    data: request,
  })

  if (res?.data) {
    return res.data
  }

  throw new Error(res?.message || 'Fail to add customer')
}

export const getUserWallet = async (): Promise<Wallet> => {
  const res = await api(`api/wallet`, {
    baseURL: process.env.NEXT_PUBLIC_FINANCE_API_URL,
    method: 'get',
  })

  if (res?.data) {
    return res.data
  }

  throw new Error(res?.message || 'Fail to get wallet')
}

export const getTransactions = async (
  request: TransactionListRequest
): Promise<TransactionListResponse> => {
  const res = await api(`api/user-transactions`, {
    baseURL: process.env.NEXT_PUBLIC_FINANCE_API_URL,
    method: 'get',
    params: request,
  })

  if (res?.data) {
    return res.data
  }

  throw new Error(res?.message || 'Fail to get transactions')
}

export const requestWithdraw = async (
  data: WithdrawRequest
): Promise<ResponseApi> => {
  const res = await api(`api/user-transaction/withdrawal`, {
    baseURL: process.env.NEXT_PUBLIC_FINANCE_API_URL,
    method: 'post',
    data: data,
  })

  if (res?.data) {
    return res.data
  }

  throw new Error(res?.message || 'Fail to request withdraw')
}

export const getCouponCustomers = async (
  request: CouponCustomerListRequest
): Promise<CouponCustomerListResponse> => {
  const res = await api(`api/coupons`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
    params: request,
  })

  if (res?.data) {
    return res.data
  }

  throw new Error(res?.message || 'Fail to get coupons')
}

export const deleteCouponCustomer = async (id: string): Promise<boolean> => {
  const res = await api(`api/coupons/${id}`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'delete',
  })
  return res && !(res instanceof Error)
}

export const addCouponCustomer = async (data: {
  fixedValue: number
  quantity: number
}): Promise<ResponseApi> => {
  const res = await api(`api/coupon`, {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'post',
    data,
  })

  if (res?.data) {
    return res.data
  }

  throw new Error(res?.message || 'Fail to add coupon')
}

export const getCouponCustomerDetail = async (id: string): Promise<CouponCustomerItem> => {
  const res = await api('api/coupons', {
    baseURL: process.env.NEXT_PUBLIC_MARKETING_API_URL,
    method: 'get',
    params: { fullTextSearch: id, page: 1, limit: 1 },
  })

  if (res?.data?.data?.[0]) return res.data.data[0]
  throw new Error(res?.message || 'Fail to get coupon detail')
}

export interface CouponOrdersResponse {
  data: Order[]
  total: number
  pages: number
  page: number
  limit: number
}

export const getOrdersByCoupon = async (params: {
  coupon: string
  page: number
  limit: number
}): Promise<CouponOrdersResponse> => {
  const res = await api(`api/orders`, {
    baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
    method: 'get',
    params,
  })

  if (res?.data) {
    return res.data
  }

  throw new Error(res?.message || 'Fail to get orders by coupon')
}
